import { supabase } from "./supabase";
import { plainError, unreachableText } from "./plainError";

const BASE = (import.meta.env.VITE_API_URL as string) || "";
const TIMEOUT_MS = 30000;

/** An API failure that still knows its HTTP status.
 *
 *  `api()` used to throw a bare Error with the status baked into the message string, so the only
 *  way to tell a 404 from a 503 was to regex the text — and nothing did. Pages therefore treated
 *  every failure identically, which is how a timed-out walkbook fetch rendered as "Walkbook not
 *  found." Subclassing Error keeps every existing `String(e.message)` call site working unchanged.
 *  status 0 = the request never reached the server (network down, or our own timeout).
 */
export class ApiError extends Error {
  status: number;
  /** What actually came back -- `API <status>: <body>` -- for logs and telemetry, never a screen.
   *  `message` is the sentence for a person (lib/plainError.ts). */
  raw: string;
  constructor(message: string, status: number, raw = message) { super(message); this.name = "ApiError"; this.status = status; this.raw = raw; }
}

/** The error a failed response becomes: a sentence for the screen, the raw text kept beside it. */
async function failed(res: Response): Promise<ApiError> {
  const body = await res.text().catch(() => "");
  return new ApiError(plainError(res.status, body), res.status, `API ${res.status}: ${body || res.statusText}`);
}

/** The error a request that never got an answer becomes. */
function unreachable(e: any): ApiError {
  const timedOut = e?.name === "AbortError";
  return new ApiError(unreachableText(timedOut), 0, timedOut ? "API request timed out" : (e?.message || "network error"));
}

/** Call the Orator FastAPI service with the caller's Supabase access token.
 *  Aborts after TIMEOUT_MS so a wedged request never hangs the UI; a caller-supplied
 *  AbortSignal (opts.signal) is respected too. */
export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  const caller = opts.signal as AbortSignal | undefined;
  if (caller) {
    if (caller.aborted) ctrl.abort();
    else caller.addEventListener("abort", () => ctrl.abort(), { once: true });
  }
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...opts,
      signal: ctrl.signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(opts.headers || {}),
      },
    });
  } catch (e: any) {
    // status 0 = never reached the server (network down, or our own timeout). Callers that need to
    // tell "gone" from "unreachable" branch on this; everything else keeps using the message.
    throw unreachable(e);
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) throw await failed(res);
  return res.json() as Promise<T>;
}

/** THE SENTENCE FOR A PERSON. Since 2026-10-30 an ApiError's `message` already is that sentence
 *  (lib/plainError.ts), so this is a convenience for catch blocks that may hold any kind of error. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return (e as any)?.message ? String((e as any).message) : String(e);
}

/** A multipart POST — the portal has had this since the background import; the dashboard needs it
 *  now that a spreadsheet is converted on the server rather than in the tab.
 *
 *  TWO THINGS IT DELIBERATELY DOES NOT DO. It sets no Content-Type, because the browser has to
 *  write the multipart boundary itself and an explicit header overwrites it with one that has no
 *  boundary in it. And it carries no TIMEOUT_MS: 30 seconds is right for a query and wrong for a
 *  40MB workbook on a hotel connection, where aborting is the failure rather than the protection. */
export async function apiForm<T = any>(path: string, form: FormData): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: "POST", body: form,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
  } catch (e: any) {
    throw unreachable(e);
  }
  if (!res.ok) throw await failed(res);
  return res.json() as Promise<T>;
}

/** A FILE FROM THE API, saved by the browser (2026-10-24: the credit history as CSV). The same
 *  token as api(); the response is kept as a blob and handed to the browser's own download. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
  } catch (e: any) {
    throw unreachable(e);
  }
  if (!res.ok) throw await failed(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
